import * as modal from "../util/modal.ts";
import * as i18n from "../i18n.ts";
import Icons from "../svg/Icons.ts";

/**
 * The inline recorder of the Voice button (FWWEB-1849): the browser transcribes what is said, and the transcript is
 * what is sent to the AI, as a TEXT import, like iOS does while the server takes no audio.
 */
const SpeechRecognitionImpl = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

const MAX_DURATION = 60000;
const COUNTDOWN_FROM = 10000;

/** false on the browsers without speech recognition (Firefox): the Voice button is not shown */
export function isVoiceAvailable() :boolean{
  return !!SpeechRecognitionImpl;
}

/**
 * Records in a card floating at the bottom of the page until Send or X.
 * @param container where the recorder card is added
 * @param onSend called with the final transcript
 */
export function record(container :HTMLElement, onSend :(transcript :string)=>void){
  if(container.querySelector('.aiimport-recorder')) return;
  const
    bar = document.createElement('flexcol'),
    recognition = new SpeechRecognitionImpl(),
    started = Date.now();
  bar.className = 'aiimport-recorder V4';
  bar.innerHTML = `<div class="aiimport-wave">${'<i></i>'.repeat(32)}</div>
    <flexrow class="aiimport-recorder-row">
      <a class="aiimport-recorder-close" title="${i18n.get('actions.cancel')}">${Icons.ic_close}</a>
      <flexauto class="aiimport-transcript"></flexauto>
      <span class="aiimport-countdown"></span>
      <button type="button" class="aiimport-recorder-send" title="${i18n.get('aiimport.voice.send')}" disabled>${Icons.ic_send}</button>
    </flexrow>`;
  container.appendChild(bar);
  const
    label = bar.querySelector('.aiimport-transcript') as HTMLElement,
    countdown = bar.querySelector('.aiimport-countdown') as HTMLElement,
    send = bar.querySelector('.aiimport-recorder-send') as HTMLButtonElement;
  label.textContent = i18n.get('aiimport.voice.listening');

  let transcript = '', stopped = false;
  const timer = setInterval(()=>{
    const left = MAX_DURATION-(Date.now()-started);
    countdown.textContent = left<=COUNTDOWN_FROM?Math.max(0,Math.ceil(left/1000))+'s':'';
    //the transcript is kept, the user can still send it
    if(left<=0) stop();
  }, 250);

  function stop(){
    if(stopped) return;
    stopped = true;
    clearInterval(timer);
    bar.classList.remove('speaking');
    bar.classList.add('stopped');
    try{ recognition.stop(); }catch(e){}
  }
  function close(){
    stop();
    recognition.onresult = recognition.onerror = recognition.onend = null;
    bar.remove();
  }

  //the language of the browser, no selector in the app
  recognition.lang = navigator.language;
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.onspeechstart = ()=>bar.classList.add('speaking');
  recognition.onspeechend = ()=>bar.classList.remove('speaking');
  recognition.onresult = (event :any)=>{
    transcript = Array.from(event.results as ArrayLike<any>).map(result=>result[0].transcript).join(' ').trim();
    if(transcript){
      label.textContent = transcript;
      send.disabled = false;
    }
  };
  recognition.onerror = (event :any)=>{
    if(event.error==='not-allowed' || event.error==='service-not-allowed'){
      close();
      //a blocked microphone cannot be asked again by the site, only from the browser settings
      modal.alert({title:'aiimport.voice.denied.title', message:'aiimport.voice.denied.body', button:[{title:'actions.gotit'}]});
    }else if(event.error!=='no-speech' && event.error!=='aborted'){
      stop();
    }
  };
  //an interruption (tab hidden, device lost) ends the recording cleanly, the transcript stays
  recognition.onend = ()=>stop();

  bar.querySelector('.aiimport-recorder-close').addEventListener('click', close);
  send.addEventListener('click', ()=>{
    const text = transcript;
    close();
    if(text) onSend(text);
  });
  recognition.start();
}
